import { FC, useContext } from "react";
import { Stack, Text, Flex, Box } from "@mantine/core";
import { CompetitionContext } from "../../../../CompetitionContext.tsx";
import { IconBallFootball } from "@tabler/icons-react";
import { useCompetition } from "../../../../hooks/useCompetition.ts";
import { useTopscorer } from "../../../../hooks/useTopscorer.ts";
import { CenteredLoader } from "../../../../Components/CenteredLoader";
import { PageTitle } from "../../../../Components/PageTitle";
import { CardContainer } from "../../../../Components/CardContainer";
import { EmptyState } from "../../../../Components/EmptyState";
import { TopscorerCard } from "../Widgets/TopscorerHighlight";
import { getPresentationParamsForOrigin } from "../../../../getPresentationParamsForOrigin.ts";

export const TopscorerPage: FC = () => {
  const competitionId = useContext(CompetitionContext);

  const competition = useCompetition(competitionId);
  const topscorerData = useTopscorer(competitionId);

  const showAssists = competition.data?.settings.topscorerCountAssists ?? true;
  const metaDescription = `Die besten Torschützen beim ${getPresentationParamsForOrigin().competitionName}.`;

  if (!topscorerData.isSuccess) {
    return (
      <Stack gap="lg" mt="md">
        <CenteredLoader />
      </Stack>
    );
  }

  const topscorer = topscorerData.data.topscorer;

  if (topscorer.length === 0) {
    return (
      <Stack gap="lg" mt="md">
        <PageTitle
          icon={<IconBallFootball size={24} />}
          metaDescription={metaDescription}
        >
          Topscorer
        </PageTitle>
        <CardContainer>
          <EmptyState message="Noch keine Tore geschossen" />
        </CardContainer>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" mt="md">
      <PageTitle
        icon={<IconBallFootball size={24} />}
        metaDescription={metaDescription}
      >
        Topscorer
      </PageTitle>

      {showAssists && (
        <Text size="sm" c="dimmed">
          Tore und Assists werden gleich gewichtet.
        </Text>
      )}

      <CardContainer>
        {/* Header Row */}
        <Flex align="center" py={4} px="xs" gap="sm">
          <Box w={24} />
          <Box w={24} />
          <Text size="xs" c="dimmed" style={{ flex: 1 }}>
            Spieler
          </Text>
          {showAssists ? (
            <>
              <Text size="xs" c="dimmed" w={24} ta="center">
                T
              </Text>
              <Text size="xs" c="dimmed" w={24} ta="center">
                A
              </Text>
              <Text size="xs" c="dimmed" w={24} ta="center">
                S
              </Text>
            </>
          ) : (
            <Text size="xs" c="dimmed" w={24} ta="center">
              T
            </Text>
          )}
        </Flex>

        {/* Scorer List */}
        <Flex direction="column" gap={4}>
          {topscorer.map((scorer) => (
            <TopscorerCard
              key={scorer.playerId}
              playerId={scorer.playerId}
              goals={scorer.goals}
              assists={scorer.assists}
              position={scorer.position}
              showAssists={showAssists}
            />
          ))}
        </Flex>
      </CardContainer>
    </Stack>
  );
};
